import React from "react";
import moment from "moment";
import { Button, Input, TextArea } from "semantic-ui-react";
import { LabelledRow } from "../components";
import ProductTypesDropdown from "../components/dropdowns/reactselect/ProductTypesDropdown";
import { OptionalDatePicker } from "../components/DatePicker/OptionalDatePicker";
import NumberInput from "../components/NumberInput";
import VatRateDropdown from "../components/dropdowns/reactselect/VatRateDropdown";
import ProductTemplateSelect from "./ProductTemplateSelect";
import PanelSegmentType from "../components/panels/PanelSegment/PanelSegment";
import { getCurrency } from "../utilities";
import SupportQuestionMark from "supportbeacon/SupportQuestionMark";

interface Props {
  initialProduct: server.dto.Product
  productsConfig: server.dto.LinkedProductPanelConfig
  productsByParent: server.dto.ProductsByParent
  onProductSaved: () => void;

  mainArea: server.dto.MainAreas;
}

interface State {
  productType: server.dto.ProductType
  ledgerNumber: string
  title: string
  description: string
  unit: string
  quantity: number
  date: moment.Moment

  costPriceVat: server.dto.VatRate
  costPricePerUnit: number

  salesPriceVat: server.dto.VatRate
  salesPricePerUnit: number

  isLoading: boolean

  ledgerHasError: boolean
  titleHasError: boolean
  dateHasError: boolean
}

export default class ProductEdit extends React.Component<Props, State> {
  ledgerRef = React.createRef<Input>();
  titleRef = React.createRef<Input>();

  state: State = {
    productType: this.props.initialProduct?.productType ?? serverReference.productTypes.find(p => p.value === "undefined"),
    ledgerNumber: this.props.initialProduct?.ledgerNumber.toString() ?? serverReference.constants.productTemplate.defaultLedgerNumber.toString(),
    title: this.props.initialProduct?.title ?? "",
    description: this.props.initialProduct?.description ?? "",
    unit: this.props.initialProduct?.unit ?? "",
    quantity: this.props.initialProduct?.quantity ?? 1,
    date: this.props.initialProduct?.date != null ? moment.utc(this.props.initialProduct.date).startOf("day") : undefined,

    costPriceVat: this.props.initialProduct?.costs?.vatRate != null
      ? this.props.productsConfig.vatRates.find(v => v.vatRateId === this.props.initialProduct.costs.vatRate.vatRateId)
      : this.props.productsConfig.vatRates[0],
    costPricePerUnit: this.props.initialProduct?.costs?.pricePerUnit.amount ?? 0,

    salesPriceVat: this.props.initialProduct?.sales?.vatRate != null
      ? this.props.productsConfig.vatRates.find(v => v.vatRateId === this.props.initialProduct.sales.vatRate.vatRateId)
      : this.props.productsConfig.vatRates[0],

    salesPricePerUnit: this.props.initialProduct?.sales?.pricePerUnit.amount ?? 0,

    isLoading: false,
    ledgerHasError: false,
    titleHasError: false,
    dateHasError: false
  };

  onTemplateSelected = (template: server.dto.ProductTemplate) => {
    const { vatRates } = this.props.productsConfig;
    this.setState({
      title: template.titleForTemplate,
      description: template.descriptionForTemplate ?? "",
      productType: template.productType,
      ledgerNumber: template.ledgerNumber.toString(),
      unit: template.unitForTemplate ?? "",
      costPricePerUnit: template.costsPricePerUnitExVat,
      costPriceVat: vatRates.find(v => v.vatRateId === template.costsVatRateId),
      salesPricePerUnit: template.salesPricePerUnitExVat,
      salesPriceVat: vatRates.find(v => v.vatRateId === template.salesVatRateId)
    });
  };

  isLedgerInvalid = (ledger: string) => ledger.trim() === "";

  isTitleInvalid = (title: string) => title.trim() === "";

  isDateInvalid = (date: moment.Moment) => date != null && (date.isBefore(moment(serverReference.constants.product.minDate)) || date.isAfter(moment(serverReference.constants.product.maxDate)));

  validate = (): boolean => {
    const isLedgerInvalid = this.isLedgerInvalid(this.state.ledgerNumber);
    const isTitleInvalid = this.isTitleInvalid(this.state.title);
    const isDateInvalid = this.isDateInvalid(this.state.date);

    isLedgerInvalid && this.ledgerRef.current?.focus();
    isTitleInvalid && this.titleRef.current?.focus();

    this.setState({
      ledgerHasError: isLedgerInvalid,
      titleHasError: isTitleInvalid,
      dateHasError: isDateInvalid,
    });

    return !isLedgerInvalid && !isTitleInvalid && !isDateInvalid;
  };

  onSave = async (e) => {
    e?.preventDefault?.();

    if (this.state.isLoading) {
      return;
    }

    if (!this.validate()) {
      return;
    }

    this.setState({ isLoading: true });

    let hasError = false;

    if (this.props.initialProduct == null) {
      const createRes = await window.CoreApi.Product.Create({
        costsPricePerUnit: this.state.costPricePerUnit,
        costsVatRateId: this.state.costPriceVat.vatRateId,
        date: this.state.date?.toDate(),
        description: this.state.description,
        ledgerNumber: Number(this.state.ledgerNumber),
        parentId: this.props.productsConfig.itemId,
        mainArea: this.props.mainArea,
        productType: this.state.productType.value,
        quantity: this.state.quantity,
        salesPricePerUnit: this.state.salesPricePerUnit,
        salesVatRateId: this.state.salesPriceVat.vatRateId,
        title: this.state.title,
        unit: this.state.unit
      });

      if (createRes.data.errors.length > 0) {
        hasError = true;
      }
    } else {
      const updateRes = await window.CoreApi.Product.Update({
        costsPricePerUnit: this.state.costPricePerUnit,
        costsVatRateId: this.state.costPriceVat.vatRateId,
        date: this.state.date?.toDate(),
        description: this.state.description,
        ledgerNumber: Number(this.state.ledgerNumber),
        productType: this.state.productType.value,
        productId: this.props.initialProduct.productId,
        quantity: this.state.quantity,
        salesPricePerUnit: this.state.salesPricePerUnit,
        salesVatRateId: this.state.salesPriceVat.vatRateId,
        title: this.state.title,
        unit: this.state.unit
      });

      if (updateRes.data.errors.length > 0) {
        hasError = true;
      }
    }

    if (hasError) {
      this.setState({ isLoading: false });
      window.displayGenericModal(RESX.ExceptionMessage.ThereIsAProblem, RESX.ExceptionMessage.ThereWasAnError, "tiny", "red");
      return;
    }

    this.setState({ isLoading: false });

    this.props.onProductSaved();
  };

  onTitleChanged = (text: string) => {
    const titleInvalid = this.isTitleInvalid(text);
    this.setState({
      titleHasError: titleInvalid,
      title: text
    });
  };

  onLedgerChanged = (text: string) => {
    const ledgerInvalid = this.isLedgerInvalid(text);
    this.setState({
      ledgerHasError: ledgerInvalid,
      ledgerNumber: text
    });
  };

  onQuantityChanged = (val: number) => {
    this.setState({
      quantity: val
    });
  };

  public render(): JSX.Element {
    return <form className="flex-column grow1" style={{ minHeight: "inherit" }} onSubmit={this.onSave}>
      <div className="flex grow1 gap-larger" style={{ minHeight: 0 }}>
        <div className="scrollbox-auto grow1" style={{ minWidth: 400, flexBasis: "0%" }}>
          <table className="s1_table bordergrid" style={{ padding: 0, background: "none", boxShadow: "inherit" }}>
            <tbody>
              <LabelledRow label={RESX.ProductTemplate.ProductType} secondColumnClass="flex gap align-center">
                <div className="grow1">
                  <ProductTypesDropdown
                    value={this.state.productType}
                    onChange={productType => { this.setState({ productType }); }}
                  />
                </div>
                <div>
                  <SupportQuestionMark openInNewTab small articleId={serverReference.supportArticleIds.whatIsProductType} />
                </div>
              </LabelledRow>
              <LabelledRow label={RESX.ProductGroup.Ledger} secondColumnClass="flex gap align-center">
                <div>
                  <NumberInput
                    innerRef={this.ledgerRef}
                    width={75}
                    value={this.state.ledgerNumber}
                    onChanged={numberValue => { this.onLedgerChanged(numberValue.formatted); }}
                    decimalPoints={0}
                    maxLength={window.serverReference.constants.productTemplate.maxLedgerNumberLength}
                    canBeEmpty
                    error={this.state.ledgerHasError}
                    canBeNegative={false}
                  />
                </div>
                <div>
                  <SupportQuestionMark openInNewTab small articleId={serverReference.supportArticleIds.whatIsTheLedgerFor} />
                </div>
              </LabelledRow>
              <LabelledRow label={RESX.GeneralLabels.Description}>
                <div className="margin-bottom-mini">
                  <Input
                    ref={this.titleRef}
                    value={this.state.title}
                    onChange={(_, { value }) => { this.onTitleChanged(value); }}
                    fluid
                    maxLength={serverReference.constants.product.maxTitleLength}
                    error={this.state.titleHasError}
                  /></div>
                <TextArea
                  value={this.state.description}
                  rows={6}
                  maxLength={serverReference.constants.product.maxDescriptionLength}
                  onChange={(_, { value }) => { this.setState({ description: value.toString() }); }}
                />
              </LabelledRow>

              <LabelledRow label={RESX.ProductTemplate.Unit} secondColumnClass="flex gap align-center">
                <div>
                  <Input
                    style={{ width: 90 }}
                    value={this.state.unit}
                    maxLength={serverReference.constants.product.maxUnitLength}
                    onChange={(_, { value }) => { this.setState({ unit: value }); }}
                  />
                </div>
                <div>
                  <SupportQuestionMark openInNewTab small articleId={serverReference.supportArticleIds.unitInProduct} />
                </div>
              </LabelledRow>

              <LabelledRow label={RESX.Financial.Quantity}>
                <NumberInput
                  width={90}
                  value={this.state.quantity}
                  onChanged={val => { this.onQuantityChanged(val.value); }}
                  decimalPoints={2}
                  defaultWhenError={1}
                />
              </LabelledRow>

              <LabelledRow label={RESX.DateTime.lblDate}>
                <OptionalDatePicker
                  validatorKey="productDate"
                  error={this.state.dateHasError}
                  minDate={moment(serverReference.constants.product.minDate)}
                  maxDate={moment(serverReference.constants.product.maxDate)}
                  value={this.state.date}
                  onChange={date => { this.setState({ date, dateHasError: false }); }}
                />
              </LabelledRow>

              {this.props.productsConfig.canReadCosts && <>
                <tr>
                  <th colSpan={3}>
                    {RESX.Financial.CostsPerUnit}
                  </th>
                </tr>
                <LabelledRow label={RESX.Financial.PriceExTax}>
                  <NumberInput
                    width={100}
                    value={this.state.costPricePerUnit}
                    onChanged={val => { this.setState({ costPricePerUnit: val.value }); }}
                    decimalPoints={2}
                    defaultWhenError={0}
                    labelContent={getCurrency(this.props.productsByParent.currency).text}
                    canBeNegative
                  />
                </LabelledRow>
                <LabelledRow label={RESX.Financial.TaxRate}>
                  <VatRateDropdown
                    options={this.props.productsConfig.vatRates}
                    onChange={vatRate => { this.setState({ costPriceVat: vatRate }); }}
                    value={this.state.costPriceVat}
                  />
                </LabelledRow>
              </>}

              {this.props.productsConfig.canReadSales && <>
                <tr>
                  <th colSpan={3}>
                    {RESX.Financial.SalesPerUnit}
                  </th>
                </tr>
                <LabelledRow label={RESX.Financial.PriceExTax}>
                  <NumberInput
                    width={100}
                    value={this.state.salesPricePerUnit}
                    onChanged={val => { this.setState({ salesPricePerUnit: val.value }); }}
                    decimalPoints={2}
                    labelContent={getCurrency(this.props.productsByParent.currency).text}
                    defaultWhenError={0}
                    canBeNegative
                  />
                </LabelledRow>
                <LabelledRow label={RESX.Financial.TaxRate}>
                  <VatRateDropdown
                    options={this.props.productsConfig.vatRates}
                    onChange={vatRate => { this.setState({ salesPriceVat: vatRate }); }}
                    value={this.state.salesPriceVat}
                  />
                </LabelledRow>
              </>}
            </tbody>
          </table>
        </div>

        <div className="grow1 scrollbox-auto" style={{ flexBasis: "0%" }}>
          <ProductTemplateSelect
            onTemplateSelected={this.onTemplateSelected}
            productGroups={this.props.productsConfig.productGroups}
            currency={this.props.productsByParent.currency}
          />
        </div>
      </div>
      <PanelSegmentType.ButtonBar className="margin-top-large">
        <Button
          primary
          onClick={this.onSave}
          loading={this.state.isLoading}

        >
          {RESX.Buttons.btnSave}
        </Button>
      </PanelSegmentType.ButtonBar>
    </form>;
  }
}
